import { addMinutes, isAfter, parseJSON } from "date-fns";
import Dashboard, {
	IOverviewChannel,
} from "../../../services/api/Dashboard";

interface ICacheInfo {
	year: number;
	month: number;
	channelType: string;
	period: 'MONTH' | 'YTD';
	expirationDate: Date;
	channel: IOverviewChannel;
}

interface ICacheChannelOverviewHandler {
  overview: IOverviewChannel;
  ytdOverview: IOverviewChannel;
  error: boolean;
}

export const invalidateChannelCache = (channelType?: string) => {
  const getStorage = sessionStorage.getItem("overview-channel");
  if (!getStorage) return;

  if (!channelType) {
    sessionStorage.removeItem("overview-channel");
    return;
  }

  let storage: Array<ICacheInfo> = [];
  try {
    storage = JSON.parse(getStorage) as Array<ICacheInfo>;
  } catch {
    sessionStorage.removeItem("overview-channel");
    return;
  }

  const filtered = storage.filter(x => x.channelType !== channelType);
  sessionStorage.setItem("overview-channel", JSON.stringify(filtered));
};

const fetchBothChannels = async (year: number, month: number, channelType: string): Promise<[IOverviewChannel, IOverviewChannel]> => {
  return Promise.all([
    Dashboard.GetChannel("LATAM", year, month, channelType),
    Dashboard.GetYTDChannel("LATAM", year, month, channelType),
  ]) as unknown as Promise<[IOverviewChannel, IOverviewChannel]>;
};

export const getOverviewChannels = async (year: number, month: number, channelType: string): Promise<ICacheChannelOverviewHandler> => {
  let overview = {} as IOverviewChannel;
  let ytdOverview = {} as IOverviewChannel;

  let storage: Array<ICacheInfo> = [];
  const getStorage = sessionStorage.getItem("overview-channel");
  const expirationDate = addMinutes(new Date(), 5);
  const now = new Date();

  if (getStorage) {
    try {
      storage = JSON.parse(getStorage) as Array<ICacheInfo>;
    } catch {
      storage = [];
      sessionStorage.removeItem("overview-channel");
    }

    // Remove legacy/corrupted entries that do not include channel type.
    storage = storage.filter(x => !!x.channelType);
    let cachedMonth = storage.filter(x =>
      x.year === year
      && x.month === month
      && x.channelType === channelType
    );

    const expDate = parseJSON(cachedMonth[0]?.expirationDate);
    if (cachedMonth.length > 0 && (isNaN(expDate.getTime()) || isAfter(now, expDate))) {
      cachedMonth = [];
      storage = storage.filter(x => x.month !== month || x.year !== year || x.channelType !== channelType);
    }

    if (cachedMonth.length === 0) {
      [overview, ytdOverview] = await fetchBothChannels(year, month, channelType);

      storage.push({ year, month, channelType, period: 'MONTH', expirationDate, channel: overview });
      storage.push({ year, month, channelType, period: 'YTD',   expirationDate, channel: ytdOverview });

      sessionStorage.setItem("overview-channel", JSON.stringify(storage));
    } else {
      const monthCache = cachedMonth.find(x => x.period === 'MONTH');
      const ytdCache = cachedMonth.find(x => x.period === 'YTD');

      if (!monthCache || !ytdCache) {
        [overview, ytdOverview] = await fetchBothChannels(year, month, channelType);

        storage = storage.filter(x => x.month !== month || x.year !== year || x.channelType !== channelType);
        storage.push({ year, month, channelType, period: 'MONTH', expirationDate, channel: overview });
        storage.push({ year, month, channelType, period: 'YTD',   expirationDate, channel: ytdOverview });
        sessionStorage.setItem("overview-channel", JSON.stringify(storage));
      } else {
        overview = monthCache.channel;
        ytdOverview = ytdCache.channel;
      }
    }
  } else {
    [overview, ytdOverview] = await fetchBothChannels(year, month, channelType);

    storage.push({ year, month, channelType, period: 'MONTH', expirationDate, channel: overview });
    storage.push({ year, month, channelType, period: 'YTD',   expirationDate, channel: ytdOverview });

    sessionStorage.setItem("overview-channel", JSON.stringify(storage));
  }

  return {
    overview,
    ytdOverview,
    error: (!overview?.id || !ytdOverview?.id)
  };
}
